import {ElementType, ReactElement, ReactNode} from 'react';
import {Nav, Row, Tab} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import clsx from 'clsx';
import {usePermissions} from '../../modules/auth/authorization/PermissionProvider';
import {checkIfAuthorized} from '../../modules/auth/authorization/api';
import styles from './TabComponent.module.scss';

/**
 */
export interface TabData {
  title: string;
  content: ReactElement;
  /** La key, qui doit être unique par rapport aux autres onglets permet de relier le bouton de changement d'onglets à son contenu correspondant **/
  key: string;
  to?: string;
  permissions?: string[];
  /** Onglet visible mais non cliquable, grisé — un écran temporairement indisponible plutôt qu'absent. */
  disabled?: boolean;
}

interface TabComponentProps {
  tabData: TabData[];
  defaultTabKey?: string;
  activeKey?: string;
  withDefaultNav?: boolean;
  onTabChange?: (key: string) => void;
  navRightContent?: ReactNode;
  /**
   * Garde les onglets sur une seule ligne et fait défiler horizontalement
   * au lieu de les laisser passer à la ligne. Utile au-delà de ~6 onglets
   * ou avec des libellés longs.
   */
  scrollable?: boolean;
}

const TabComponent = ({
  tabData,
  defaultTabKey,
  activeKey,
  onTabChange,
  withDefaultNav = true,
  navRightContent,
  scrollable = false,
}: TabComponentProps): JSX.Element => {
  const userPermissions = usePermissions();

  return (
    <Tab.Container
      defaultActiveKey={defaultTabKey ?? tabData[0]?.key}
      transition={false}
      activeKey={activeKey}
      onSelect={(key) => {
        if (key && onTabChange) {
          onTabChange(key);
        }
      }}
      mountOnEnter={true}
    >
      {withDefaultNav && (
        <div
          className={clsx(
            'd-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between mb-5',
            {'overflow-auto': scrollable}
          )}
        >
          <Nav
            className={clsx(
              'nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 mb-0 flex-grow-1',
              styles.tabsScroll,
              {'flex-nowrap': scrollable}
            )}
          >
            {tabData
              .filter(({permissions}) => {
                return !permissions || checkIfAuthorized(userPermissions, permissions);
              })
              .map(({title, to, key, disabled}) => {
                return (
                  <Nav.Item key={to + title} className={disabled ? undefined : 'cursor-pointer'}>
                    <Nav.Link
                      {...(!onTabChange && to && {as: Link as ElementType, to})}
                      eventKey={key}
                      disabled={disabled}
                    >
                      <div className={clsx('fw-bold fs-5 lh-1 p-2', {'text-nowrap': scrollable})}>
                        {title}
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
          </Nav>
          {navRightContent && (
            <div className="ms-3 d-flex align-items-center">{navRightContent}</div>
          )}
        </div>
      )}
      <Row className="g-0 mx-0">
        <Tab.Content className={'pe-0'}>
          {tabData.map(({key, content, to}) => {
            return (
              <Tab.Pane eventKey={key} key={to + key}>
                {content}
              </Tab.Pane>
            );
          })}
        </Tab.Content>
      </Row>
    </Tab.Container>
  );
};
export default TabComponent;
